/**
 * Workforce Hub W2 — read-only Audit tab.
 *
 * Shows the last 100 audit entries tied to an employee profile,
 * server-filtered to entity_type='employee_profile' AND entity_id=:id
 * AND company_id=req.user.companyId. No UI redesign — the tab
 * follows the existing Hub tab anatomy and uses StatusBanner /
 * EmptyState / LoadingSkeleton primitives.
 */

import React from 'react';
import Card from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import StatusBanner from '../../components/ui/StatusBanner';
import { useEmployeeAudit } from '../../hooks/useEmployeeAudit';

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function EmployeeAuditTab({ profileId }) {
  const { entries, status, error, refetch } = useEmployeeAudit(profileId);

  if (status === 'forbidden') {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-audit-forbidden">
        <EmptyState
          icon="·"
          headline="Audit access restricted"
          body="You don't have permission to view this employee's audit trail. Ask an admin if you need access."
        />
      </Card>
    );
  }

  if (status === 'error') {
    return (
      <Card padding="md" shadow="sm">
        <StatusBanner kind="error" message={error} onRetry={refetch} />
      </Card>
    );
  }

  if (status === 'loading' && entries.length === 0) {
    return (
      <Card padding="md" shadow="sm">
        <LoadingSkeleton variant="paragraph" lines={4} />
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card padding="md" shadow="sm" data-testid="employee-audit-empty">
        <EmptyState
          icon="·"
          headline="No audit activity yet"
          body="Profile edits, document uploads, photo changes, and access events will appear here as operators work on this employee."
        />
      </Card>
    );
  }

  return (
    <Card padding="none" shadow="sm" data-testid="employee-audit-panel">
      <ul className="employee-audit__list" aria-label="Employee audit trail">
        {entries.map(entry => (
          <li key={entry.id} className="employee-audit__row" data-testid={`employee-audit-entry-${entry.id}`}>
            <div className="employee-audit__row-meta">
              <span className="employee-audit__row-when">{formatDateTime(entry.createdAt)}</span>
              <span className="employee-audit__row-actor">{entry.actorEmail || 'system'}</span>
            </div>
            <div className="employee-audit__row-action">{entry.action}</div>
            {entry.summary ? (
              <div className="employee-audit__row-summary">{entry.summary}</div>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}
